// apps/cli/src/commands/enterprise/campaigns/lifecycle.ts
import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import { unwrapDataResponse } from '@wport/core';
import { enterprisePatch } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { printJson } from '../../../lib/output';
import { requireEncId, type WriteCtx } from '../jobs/write-shared';
import type { CreatedCampaign } from './create';

interface LifecycleFlags {
	idempotencyKey?: string;
}

/**
 * publish / unpublish 共用：PATCH /campaigns/:enc_id/{action}（空 body，帶 Idempotency-Key）。
 * 可逆操作 → 不需 --confirm（有別於 jobs delete）。publish 超過 10 active 時後端回 400 → 透傳。
 */
export async function runCampaignTransition(
	ctx: WriteCtx,
	apiKey: string,
	encId: string,
	action: 'publish' | 'unpublish',
	idempotencyKey: string
): Promise<void> {
	const trimmed = requireEncId(encId);
	const { body } = await enterprisePatch(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		`/campaigns/${encodeURIComponent(trimmed)}/${action}`,
		{},
		{ idempotencyKey }
	);
	const result = unwrapDataResponse<CreatedCampaign>(body);
	if (ctx.format === 'json') {
		printJson(result);
		return;
	}
	const verb = action === 'publish' ? 'Published' : 'Unpublished';
	process.stdout.write(`${verb} campaign: ${result.enc_id ?? trimmed}\n`);
}

export function registerEnterpriseCampaignsPublish(parent: Command): void {
	parent
		.command('publish <enc_id>')
		.description('Publish (activate) a recruitment campaign')
		.option('--idempotency-key <key>', 'reuse across retries (default: a fresh UUID)')
		.action(async (encId: string, flags: LifecycleFlags, command: Command) => {
			const ctx = resolveContext(command);
			const { key } = resolveApiKey((command.optsWithGlobals() as { apiKey?: string }).apiKey);
			await runCampaignTransition(ctx, key, encId, 'publish', flags.idempotencyKey ?? randomUUID());
		});
}

export function registerEnterpriseCampaignsUnpublish(parent: Command): void {
	parent
		.command('unpublish <enc_id>')
		.description('Unpublish (deactivate) a recruitment campaign')
		.option('--idempotency-key <key>', 'reuse across retries (default: a fresh UUID)')
		.action(async (encId: string, flags: LifecycleFlags, command: Command) => {
			const ctx = resolveContext(command);
			const { key } = resolveApiKey((command.optsWithGlobals() as { apiKey?: string }).apiKey);
			await runCampaignTransition(ctx, key, encId, 'unpublish', flags.idempotencyKey ?? randomUUID());
		});
}
